<template>
  <div :class="$style.container">
    <div
      :class="[
        $style.dropdown,
        { [$style.desktop]: desktop, [$style.loggedIn]: isLoggedIn }
      ]"
    >
      <!-- LOGGED OUT STATE -->
      <!-- ---------------- -->

      <template v-if="!isLoggedIn">
        <button
          class="hit-solid fluid"
          :class="$style.accentSolid"
          @click="handleSignIn"
        >
          {{ translations.signIn }}
        </button>
        <slot v-if="!hideSignUp" name="new-customer">
          <div v-if="signUpUrl" :class="$style.newCustomer">
            {{ translations.newCustomerLabel }}
            <a :class="$style.signupLink" :href="signUpUrl">
              {{ translations.signUpTodayLabel }}
            </a>
          </div>
        </slot>
      </template>

      <!-- LOGGED IN STATE -->
      <!-- --------------- -->

      <div v-if="isLoggedIn">
        <!-- LOYALTY -->

        <div v-if="!desktop && _marketLoyalty.enabled" :class="$style.loyalty">
          <a :class="$style.loyaltyLink" :href="rewardsPageUrl">
            <span :class="$style.loyaltyText">
              {{ translations.enjoyLoyalty }}
            </span>
            <span v-if="showPoints" :class="$style.loyaltyAvailableBalance">
              {{ loyaltyAmount }}
            </span>
          </a>
        </div>

        <!-- ID & VOLUMES & NAV -->

        <div>
          <!-- ID -->
          <div v-if="showDistPhotoNameId" :class="$style.head">
            <div v-if="desktop" :class="$style.headLeft">
              <div
                v-if="user.photoURL"
                :class="$style.profileImage"
                :style="`background-image: url(${user.photoURL};`"
                data-testid="profileImage"
              />
              <NsIcon
                v-else
                :class="$style.profileIcon"
                icon-name="icon-profile"
                size="custom"
              />
            </div>
            <div>
              <div v-if="desktop" :class="$style.userName">{{ username }}</div>
              {{ translations.id }}: {{ user.id }}
            </div>
          </div>

          <!-- VOLUMES -->
          <div v-if="showVolumes" :class="$style.userVolumes">
            <div
              v-for="volume in userVolumes"
              :key="volume.name"
              :class="$style.volume"
            >
              <span :class="$style.volumeName">{{ volume.name }}</span>
              <span :class="$style.volumeValue">{{ volume.value }}</span>
            </div>
          </div>

          <!-- NAV -->
          <ul v-if="!isGuest" :class="$style.nav">
            <li v-for="menuItem in filteredProfileMenu" :key="menuItem.name">
              <a :class="$style.navLink" :href="menuItem.href">
                {{ menuItem.name }}
              </a>
            </li>
            <li>
              <a
                :href="signOutUrl"
                :class="$style.navLink"
                @click="event => handleSignOut(event)"
              >
                {{ translations.signOut }}
              </a>
            </li>
          </ul>
          <ul v-else :class="$style.nav">
            <li>
              <a
                :href="signOutUrl"
                :class="$style.navLink"
                @click="event => handleSignOut(event)"
              >
                {{ translations.signOut }}
              </a>
            </li>
          </ul>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { events, util } from "@nuskin/ns-util";
import {
  AccountManager,
  UserService,
  VGDistInfoService
} from "@nuskin/ns-account";
import { CurrencyService } from "@nuskin/ns-shop";
import webLoyalty from "@nuskin/ns-loyalty-web";
import { NsIcon } from "@nuskin/design-components";
import { isNullOrEmpty } from "@nuskin/ns-common-lib";

import vueEventBus from "./EventBus";

export default {
  name: "NsHeaderProfile",
  components: {
    NsIcon
  },
  props: {
    /**
     * Hides the Signup text and link
     */
    hideSignUp: Boolean,

    /**
     * Overrides service call for market loyalty data
     * ({ enabled, landingPage, dashboardPage })
     */
    marketLoyalty: {
      type: Object,
      default: null
    },

    /**
     * List of potential navigation links
     */
    profileMenu: {
      type: Array,
      default: () => []
    },

    /**
    /**
     * The url where signout link takes you
     */
    signOutUrl: {
      type: String,
      default: ""
    },

    /**
     * The url where signup link takes you
     */
    signUpUrl: {
      type: String,
      default: ""
    },

    /**
     * Translations for component
     * Required keys: id, enjoyLoyalty, newCustomerLabel,
     * signUpTodayLabel, signIn, signOut, signUp
     */
    translations: {
      type: Object,
      default: () => ({})
    },

    /**
     * Overrides service call for user loyalty data ({ optedIn, wallet: {} })
     */
    userLoyalty: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      marketLoyaltyFromService: {
        enabled: false,
        landingPage: "",
        dashboardPage: ""
      },
      preferredName: "",
      isLoggedIn: false,
      user: null,
      userLoyaltyFromService: {
        optedIn: false,
        wallet: {
          available: null
        }
      },
      userVolumes: []
    };
  },
  computed: {
    desktop() {
      return this.$mq === "desktop" || this.$mq === "desktopHD";
    },

    runConfig() {
      return this.$NsAppService.runConfig || {};
    },

    marketConfig() {
      return this.$NsAppService.marketConfig || {};
    },

    filteredProfileMenu() {
      return this.profileMenu.filter(pm =>
        pm.accountLevels.some(
          al => this.isLoggedIn && !!this.user && this.user.accountType === al
        )
      );
    },

    isInUserMarket() {
      return (
        this.isLoggedIn &&
        !!this.user &&
        !!this.runConfig &&
        this.user.countryCd === this.runConfig.country
      );
    },

    _marketLoyalty() {
      return this.marketLoyalty || this.marketLoyaltyFromService;
    },

    _userLoyalty() {
      return this.userLoyalty || this.userLoyaltyFromService;
    },

    loyaltyAmount() {
      return CurrencyService.format(this._userLoyalty.wallet.available);
    },

    rewardsPageUrl() {
      const language = this.runConfig.language || "en";
      const market = this.runConfig.country || "US";
      const localeCode = `${language}_${market}`;

      let url = this.showPoints
        ? this._marketLoyalty.dashboardPage
        : this._marketLoyalty.landingPage;
      return url.replace("{locale}", localeCode);
    },

    showDistPhotoNameId() {
      return (
        this.isLoggedIn &&
        !!this.user &&
        (this.user.accountType === "10" ||
          (util.countryCode === "JP" && this.user.accountType === "30"))
      );
    },

    showPoints() {
      return (
        this.isInUserMarket &&
        this._userLoyalty.optedIn &&
        !!this._userLoyalty.wallet.available
      );
    },

    showVolumes() {
      return (
        this.isLoggedIn &&
        !!this.user &&
        this.user.accountType === "10" &&
        this.userVolumes &&
        this.userVolumes.length > 0
      );
    },

    username() {
      return (
        this.preferredName ||
        (this.isLoggedIn &&
          !!this.user &&
          (this.user.displayName ||
            this.user.localName ||
            this.user.lastName + ", " + this.user.firstName))
      );
    },
    isGuest() {
      return !!this.user && !!this.user.isGuest;
    }
  },
  mounted() {
    this.addListeners();
  },
  destroyed() {
    this.removeListeners();
  },
  methods: {
    init() {
      this.setUser();

      // if userLoyalty is not provided in props, call service
      if (
        !this.userLoyalty ||
        (this.userLoyalty.optedIn && !this.marketLoyalty)
      ) {
        this.getLoyaltyData();
      }
    },

    handleSignIn() {
      events.publish(events.authentication.REQUEST_FOR_LOGIN, {
        href: window.location.href
      });
    },

    handleSignOut(event) {
      event.preventDefault();
      const signOutUrl =
        this.signOutUrl === "#"
          ? util.getLocaleUrl() + "home.html"
          : this.signOutUrl;
      AccountManager.afterLogoutRedirect = signOutUrl;
      AccountManager.logout();
    },

    getLoyaltyData() {
      if (!this.marketLoyalty) {
        this.marketLoyaltyFromService = this.marketConfig.loyalty || {
          enabled: false,
          landingPage: "",
          dashboardPage: ""
        };
      }

      if (this._marketLoyalty.enabled && !this.userLoyalty) {
        this.getUserLoyaltyValues();
      }
    },

    getUserLoyaltyValues() {
      const userLoyaltyData = webLoyalty.getLoyaltyRewardsPoints();

      if (userLoyaltyData && userLoyaltyData.optInStatus) {
        this.userLoyaltyFromService = {
          optedIn: true,
          wallet: {
            available: userLoyaltyData.wallet.available
          }
        };
      }
    },

    setUser() {
      this.isLoggedIn = UserService.isLoggedIn();
      if (this.isLoggedIn) {
        this.user = UserService.getUser();
        this.setUserProfileImageNameAndVolumes();
      } else {
        this.user = null;
      }
    },

    async setUserProfileImageNameAndVolumes() {
      if (
        this.isLoggedIn &&
        !!this.user &&
        (!this.user.photoURL || !this.user.displayName || !this.user.volumes) &&
        !this.user.isVolumesLoaded
      ) {
        const distInfo = await VGDistInfoService.getVGDistInformation();

        if (distInfo) {
          if (distInfo.photoURL && !this.user.photoURL) {
            this.user = {
              ...this.user,
              photoURL: distInfo.photoURL
            };
          }

          if (distInfo.displayName && !this.user.displayName) {
            this.user = {
              ...this.user,
              displayName: distInfo.displayName
            };
          }
        }
      }

      this.setUserVolumes();

      if (isNullOrEmpty(this.userVolumes)) {
        // checkVolumeData fires the VOLUME_ADDED and LOGIN_COMPLETE events
        await VGDistInfoService.checkVolumeData();
      }
    },

    setUserVolumes() {
      if (this.isLoggedIn && !!this.user && !!this.user.volumes) {
        if (Array.isArray(this.user.volumes)) {
          this.userVolumes = this.user.volumes;
        } else {
          this.userVolumes = Object.entries(this.user.volumes).map(
            ([key, value]) => ({
              name: key,
              value
            })
          );
        }
      } else {
        this.userVolumes = [];
      }
    },

    handleUserChange() {
      this.setUser();
    },

    handleProfileImageUpdate(profileImageUrl) {
      this.user = {
        ...this.user,
        photoURL: profileImageUrl
      };
    },

    handlePreferredNameUpdate(preferredName) {
      this.preferredName = preferredName;
    },

    handleVolumesAdded(user) {
      this.user = {
        ...this.user,
        ...user
      };

      this.setUserVolumes();
    },

    handleLoyaltyDataUpdate() {
      this.getUserLoyaltyValues();
    },

    /**
     * Subscribe to events, should only be called when mounted
     */
    addListeners() {
      // watch app service to finish loading
      this.$watch(
        function() {
          return !this.$NsAppService.loading;
        },
        this.init,
        {
          immediate: true
        }
      );

      // listen to config changes
      vueEventBus.$on("config-changed", this.setUser);

      // listen to auth (login/logout) changes
      events.subscribe(events.authentication.LOGIN, this.handleUserChange);
      events.subscribe(events.authentication.LOGOUT, this.handleUserChange);

      // listen to profile image updates
      events.subscribe(
        events.account.PROFILE_IMAGE_UPDATED,
        this.handleProfileImageUpdate
      );

      // listen to preferred name updates
      events.subscribe(
        events.account.PREFERRED_NAME_UPDATE,
        this.handlePreferredNameUpdate
      );

      // listen to volumes being added
      events.subscribe(events.account.VOLUME_ADDED, this.handleVolumesAdded);

      // listen to loyalty updates if userLoyalty is not provided in props
      if (!this.userLoyalty) {
        events.subscribe(
          events.loyalty.LOYALTY_DATA_UPDATED,
          this.handleLoyaltyDataUpdate
        );
      }
    },

    /**
     * Unsubscribe from events, should only be called when destroyed
     */
    removeListeners() {
      // stop listening to event bus
      vueEventBus.$off();

      // stop listening to auth changes
      events.unsubscribe(events.authentication.LOGIN, this.updateUser);
      events.unsubscribe(events.authentication.LOGOUT, this.updateUser);

      // stop listening to profile image updates
      events.unsubscribe(
        events.account.PROFILE_IMAGE_UPDATED,
        this.handleProfileImageUpdate
      );

      // stop listening to preferred name updates
      events.unsubscribe(
        events.account.PREFERRED_NAME_UPDATE,
        this.handlePreferredNameUpdate
      );

      // stop listening to volumes being added
      events.unsubscribe(events.account.VOLUME_ADDED, this.handleVolumesAdded);

      // stop listening to loyalty updates
      events.unsubscribe(
        events.loyalty.LOYALTY_DATA_UPDATED,
        this.handleLoyaltyDataUpdate
      );
    }
  }
};
</script>

<style lang="scss" module>
@use "sass:map";
@use "~@nuskin/ns-core-styles/src/scss/global.mixins";
@use "~@nuskin/ns-core-styles/src/scss/globalVariables";
@use "~@nuskin/ns-core-styles/src/scss/colors";
@use "~@nuskin/ns-core-styles/src/scss/other";
@use "~@nuskin/ns-core-styles/src/scss/typographyVariables";

* {
  box-sizing: border-box;
  font-family: var(--font-family-primary);
}

.dropdown {
  padding: 10px 15px 15px;
}

.newCustomer {
  margin-top: 15px;
  color: map.get(colors.$themeColors, color-interactive-7);
  font-size: 13px;
  line-height: 16px;
}

a.signupLink {
  @include global.p-medium;
  font-size: 13px;
  line-height: 16px;
  text-decoration: none !important;
  color: map.get(colors.$themeColors, color-expressive-6);
  position: relative;

  &:focus,
  &:hover {
    outline: none;
  }

  &:after {
    content: "";
    position: absolute;
    bottom: -2px;
    left: 0;
    right: 0;
    width: 100%;
    height: 1px;
    background-color: map.get(colors.$themeColors, color-expressive-6);
    transition: map.get(other.$otherUtils, global-transition);
    opacity: 0;
    visibility: hidden;
  }

  &:hover::after {
    opacity: 1;
    visibility: visible;
  }
}

// nested for specificity
.dropdown .accentSolid {
  background-color: map.get(colors.$themeColors, color-expressive-6);
  border-color: map.get(colors.$themeColors, color-expressive-6);
  color: #fff;
}

.loyalty {
  margin-bottom: 10px;
  padding-left: 13px;
  font-size: 13px;
}

.loyaltyLink {
  text-decoration: none;
  color: map.get(colors.$baseColors, blackhole);
}

.loyaltyText {
  @include global.subtitle-small-bold-caps;
}

.loyaltyAvailableBalance {
  background-color: map.get(colors.$baseColors, blackhole);
  color: white;
  padding: 0px 9px;
  border-radius: 20px;
  font-weight: map.get(typographyVariables.$typography, font-weight-semibold);
  font-size: map.get(
    typographyVariables.$typography,
    subtitle-small-bold-caps_-_font-size
  );
  margin-left: 10px;
}

.head {
  display: flex;
  align-items: center;
  padding: 13px;
  width: 100%;
  color: map.get(colors.$themeColors, color-interactive-7);
  background-color: map.get(colors.$themeColors, color-background-1);
  font-size: 13px;
  line-height: 16px;
  letter-spacing: 0.15px;
  text-transform: uppercase;
}

.headLeft {
  margin-right: 15px;
}

.profileImage {
  border: 1px solid map.get(colors.$themeColors, color-interactive-8);
  border-radius: 50%;
  height: 26px;
  width: 26px;
  overflow: hidden;
  background-repeat: no-repeat;
  background-position: center;
  background-size: cover;
  z-index: 1;
}

// nested for specificity
.headLeft .profileIcon {
  width: 26px;
  height: 26px;
}

.userName {
  font-size: 18px;
  line-height: 22px;
  letter-spacing: 0.35px;
}

.userVolumes {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  background-color: map.get(colors.$themeColors, color-gray-4);
  color: map.get(colors.$themeColors, color-interactive-7);
}

.head,
.userVolumes {
  user-select: text;
}

.volume {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 13px;
}

.volumeName {
  font-size: 12px;
  font-weight: bold;
  letter-spacing: 0.8px;
  line-height: 22px;
  text-transform: uppercase;
}

.volumeValue {
  font-size: 14px;
  line-height: 14px;
}

.nav {
  margin: 13px 0;
  list-style: none;
}

a.navLink {
  display: block;
  padding: 6px 13px;
  color: map.get(colors.$themeColors, color-interactive-7);
  font-size: 14px;
  font-weight: 400;
  text-decoration: none !important;
  transition: map.get(other.$otherUtils, global-transition);

  &:focus,
  &:hover {
    outline: none;
  }
}

/** ************************* */
/** DESKTOP STYLE ADJUSTMENTS */
/** ************************* */

.dropdown.desktop {
  padding: 25px;
  min-width: 280px;

  &.loggedIn {
    padding: 0;
  }

  .head {
    padding: 15px 25px;
  }

  .volume {
    padding: 15px 25px;
  }

  .nav {
    margin: 15px 0;
  }

  a.navLink {
    padding: 10px 25px;
    font-size: 16px;
  }
}
</style>
